import { useCallback, useMemo } from "react";

import { faArrowUpRight, faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";

import { Icon } from "src/base-components/Icon";
import { Cell, Header, TableComp as Table } from "src/base-components/Table";
import { CaseStatusPill } from "src/cases/common/CaseStatusPill";
import { deserializeCaseTemplates } from "src/cases/hooks";
import { useCaseTemplatesRaw } from "src/cases/queries/templates";
import { CasePropertySchema } from "src/cases/queues/types";
import { getPropertyVariant } from "src/cases/utils";
import { TruncatedIdPill } from "src/decisionsOverview/TruncatedIdPill";
import { Modal } from "src/design-system/Modal";
import { getBaseUrl, getUrlToCaseViewPage } from "src/router/urls";

export type BulkActionErrorCaseData = {
  id: string;
  status: string;
  sub_status?: string;
  templateId: string;
  versionId: string;
};

export enum BulkActionErrorType {
  INCOMPLETE_TASKS = "incomplete_tasks",
  CONFLICT = "conflict",
}

type BulkActionErrorModalProps = {
  isOpen: boolean;
  errorType: BulkActionErrorType;
  cases: BulkActionErrorCaseData[];
  wsId: string;
  orgId: string;
  onClose: () => void;
};

const columnHelper = createColumnHelper<BulkActionErrorCaseData>();

const ERROR_CONFIG = {
  [BulkActionErrorType.INCOMPLETE_TASKS]: {
    title: "Cases with incomplete required tasks",
    description:
      "Complete the required tasks for these cases to apply the action.",
  },
  [BulkActionErrorType.CONFLICT]: {
    title: "Cases with an action in progress",
    description:
      "These cases already have an action running. You can't start another action until the current one is finished.",
  },
};

export const BulkActionErrorModal: React.FC<BulkActionErrorModalProps> = ({
  isOpen,
  errorType,
  cases,
  wsId,
  orgId,
  onClose,
}) => {
  const { data: caseTemplates } = useCaseTemplatesRaw({
    select: deserializeCaseTemplates,
  });

  const openCaseInNewTab = useCallback(
    (caseId: string) => {
      const fullUrl = getBaseUrl() + getUrlToCaseViewPage(orgId, wsId, caseId);
      window.open(fullUrl, "_blank");
    },
    [orgId, wsId],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("id", {
        header: (info) => (
          <Header dataLoc="bulk-action-error-header-id" info={info}>
            <span className="whitespace-nowrap">Case ID</span>
          </Header>
        ),
        cell: (info) => (
          <Cell className="w-24" info={info}>
            <TruncatedIdPill id={info.getValue()} />
          </Cell>
        ),
      }),
      columnHelper.accessor("status", {
        header: (info) => (
          <Header dataLoc="bulk-action-error-header-status" info={info}>
            <div className="flex items-center gap-x-0.5">
              <Icon color="text-gray-500" icon={faSpinner} size="2xs" />
              Status
            </div>
          </Header>
        ),
        cell: (info) => {
          const caseData = info.row.original;
          const template = caseTemplates?.data?.find(
            (t) => t.id === caseData.templateId,
          );
          const version = template?.versions?.find(
            (v) => v.id === caseData.versionId,
          );
          const variant = getPropertyVariant(
            caseData.status,
            version?.case_properties?.status as CasePropertySchema,
          );

          return (
            <Cell
              dataLoc={`bulk-action-error-cell-status-${info.row.id}`}
              info={info}
            >
              <CaseStatusPill
                status={caseData.status}
                subStatus={caseData.sub_status}
                variant={variant}
              />
            </Cell>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        cell: ({ row }) => (
          <Icon
            color="text-gray-500"
            icon={faArrowUpRight}
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              openCaseInNewTab(row.original.id);
            }}
          />
        ),
        size: 50,
      }),
    ],
    [caseTemplates, openCaseInNewTab],
  );

  const config = ERROR_CONFIG[errorType];

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header description={config.description}>
        {config.title}
      </Modal.Header>
      <Modal.Content>
        <Table columns={columns} data={cases} variant="compact" />
      </Modal.Content>
      <Modal.Footer primaryButton={null} secondaryButton="Close" />
    </Modal>
  );
};
